import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
// Tokens first, then primitives, then the legacy stylesheet. Loading in this
// order lets the app migrate screen by screen instead of needing a flag day.
import './design/tokens.css';
import './design/primitives.css';
import './styles.css';
import { AuthProvider } from './auth/AuthProvider';
import { SyncProvider } from './sync/SyncProvider';

/**
 * The gallery is a development-only route, reached at `?gallery`. It is the
 * contract the app is migrated against and the surface visual review happens on;
 * it is never part of a learner's build.
 *
 * Loaded dynamically, stylesheet included. Gating only the component behind
 * `import.meta.env.DEV` removed its JavaScript from the bundle but not its CSS —
 * a static stylesheet import is unconditional, so every learner was downloading
 * styles for a page they can never reach.
 */
const showGallery =
  import.meta.env.DEV && new URLSearchParams(window.location.search).has('gallery');

const root = createRoot(document.getElementById('root')!);

if (showGallery) {
  void Promise.all([import('./design/Gallery'), import('./design/gallery.css')]).then(
    ([module]) => {
      root.render(
        <StrictMode>
          <module.Gallery />
        </StrictMode>,
      );
    },
  );
} else {
  root.render(
    <StrictMode>
      {/* The auth provider resolves the session in the background; it never
          gates the first paint, so a signed-out learner reaches the app
          immediately. The sync provider sits inside it because the engine is
          per-account, and there is no engine at all while signed out. */}
      <AuthProvider>
        <SyncProvider>
          <App />
        </SyncProvider>
      </AuthProvider>
    </StrictMode>,
  );
}

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js');
  });
}
